<template>
    <div>
        <button 
            type="button" 
            class="btn btn-link hover:opacity-50 mb-2"
            @click="onlineCurriculum.isOpen = true"
        >
            <PencilAltIcon class="w-6 h-6 text-gray-500 mr-2" />
            Edit online curriculum number and options
        </button>
        <button 
            type="button" 
            class="btn btn-link hover:opacity-50"
            @click="usernamePasswordFields.isOpen = true"
        >
            <PencilAltIcon class="w-6 h-6 text-gray-500 mr-2" />
            Edit username and password fields for online curriculum
        </button>
    </div>
    <TransitionRoot appear :show="onlineCurriculum.isOpen" as="template">
        <Dialog as="div" class="relative z-10">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <FormKit 
                    type="form"
                    :actions="false"
                    @submit="saveOnlineCurriculumOptions"
                    :errors="['']"
                    form-class="flex min-h-full items-center justify-center p-4 text-center"
                    :classes="{
                        message: 'hidden'
                    }"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel
                            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                        >
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-gray-900"
                            >
                                Configure Online Curriculum
                            </DialogTitle>

                            <div class="mt-6">
                                <div class="grid grid-cols-1 gap-4">
                                    <div 
                                        v-for="(onlineCurriculumOption, index) in onlineCurriculum.options"
                                        :key="`${onlineCurriculumOption.name}`"
                                    >
                                        <FormKit
                                            type="text"
                                            :label="`Online Curriculum ${index + 1} Field Name`"
                                            v-model="onlineCurriculum.options[index].label"
                                            validation="required|string"
                                            :classes="formElement"
                                        />
                                        <div 
                                            v-if="index + 1 === onlineCurriculum.options.length"
                                            :class="['flex justify-end']"
                                        >
                                             <button
                                                type="button"
                                                class="btn btn-link text-red-500"
                                                @click="removeOnlineCurriculumOption()"
                                            >
                                                <XIcon class="w-4 h-4 mr-1" /> Remove
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="onlineCurriculum.options.length <= 0" class="flex items-center justify-center">
                                        <p class="text-sm text-gray-400">No options available</p>
                                    </div>
                                    <div :class="['flex justify-center']">
                                        <button
                                            type="button"
                                            class="btn btn-link hover:bg-gray-100"
                                            @click="addOnlineCurriculumOption()"
                                        >
                                            <PlusCircleIcon class="w-6 h-6 mr-2" /> Add Options
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <button 
                                    type="button" 
                                    class="btn btn-white"
                                    @click="closeOnlineCurriculumOptions()"
                                >
                                    <XIcon class="w-5 h-5 mr-2" />
                                    Close
                                </button>
                                <button 
                                    type="submit" 
                                    class="btn btn-primary"
                                >
                                    Save
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </FormKit>
            </div>
        </Dialog>
    </TransitionRoot>
    <TransitionRoot appear :show="usernamePasswordFields.isOpen" as="template">
        <Dialog as="div" class="relative z-10">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <FormKit 
                    type="form"
                    :actions="false"
                    validation-visibility="submit"
                    @submit="saveUsernamePasswordFields"
                    :errors="['']"
                    form-class="flex min-h-full items-center justify-center p-4 text-center"
                    :classes="{
                        message: 'hidden'
                    }"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel
                            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                        >
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-gray-900"
                            >
                                Configure Online Curriculum Usernames & Passwords
                            </DialogTitle>

                            <div class="mt-6">
                                <div class="grid grid-cols-1 gap-4">
                                    <template v-if="usernamePasswordFields.fields.length > 0">
                                        <div 
                                            v-for="(usernamePasswordField, index) in usernamePasswordFields.fields"
                                            :key="`${usernamePasswordField.group}`"
                                        >
                                            <FormKit
                                                type="text"
                                                :label="usernamePasswordFields.fields[index].fields[0].title"
                                                v-model="usernamePasswordFields.fields[index].fields[0].label"
                                                validation="required|string"
                                                validation-visibility="submit"
                                                :classes="{
                                                    ...formElement,
                                                    outer: 'mb-4'
                                                }"
                                            />
                                            <FormKit
                                                type="text"
                                                :label="usernamePasswordFields.fields[index].fields[1].title"
                                                v-model="usernamePasswordFields.fields[index].fields[1].label"
                                                validation="required|string"
                                                validation-visibility="submit"
                                                :classes="{
                                                    ...formElement,
                                                    outer: 'mb-4'
                                                }"
                                            />
                                            <FormKit
                                                type="text"
                                                :label="usernamePasswordFields.fields[index].fields[2].title"
                                                v-model="usernamePasswordFields.fields[index].fields[2].label"
                                                validation="required|string"
                                                validation-visibility="submit"
                                                :classes="{
                                                    ...formElement,
                                                    outer: 'mb-4'
                                                }"
                                            />
                                            <div 
                                                class="mt-6 border"
                                            >
                                            </div>
                                            <div 
                                                v-if="index + 1 === usernamePasswordFields.fields.length"
                                                :class="['flex justify-end']"
                                                @click="removeUsernamePasswordFields()"
                                            >
                                                <button
                                                    type="button"
                                                    class="btn btn-link text-red-500"
                                                >
                                                    <XIcon class="w-4 h-4 mr-1" /> Remove
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                    <div v-if=" usernamePasswordFields.fields.length <= 0" class="flex items-center justify-center">
                                        <p class="text-sm text-gray-400">No username & password available</p>
                                    </div>
                                    <div :class="['grid grid-cols-1']">
                                        <button
                                            type="button"
                                            class="btn btn-link"
                                            @click="addUsernamePasswordFields()"
                                        >
                                            <PlusCircleIcon class="w-6 h-6 mr-2" /> Add username & password
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <button 
                                    type="button" 
                                    class="btn btn-white"
                                    @click="closeUsernamePasswordFields()"
                                >
                                    <XIcon class="w-5 h-5 mr-2" />
                                    Close
                                </button>
                                <button 
                                    type="submit" 
                                    class="btn btn-primary"
                                >
                                    Save
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </FormKit>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import axios from 'axios'
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle
} from '@headlessui/vue'
import { PencilAltIcon, PlusCircleIcon, XIcon, MinusIcon } from '@heroicons/vue/outline'
import { useFormStyles } from '@/composables/styles.js'
import { useAlert } from '@/composables/alert.js'

const { formElement } = useFormStyles()

const { showAlert, confirm } = useAlert()

const onlineCurriculum = reactive({
    options: [],
    removed: [],
    isOpen: false,
    fetching: false,
    saving: false,
})

const usernamePasswordFields = reactive({
    fields: [],
    removed: [],
    isOpen: false,
    fetching: false,
    saving: false,
})

onMounted(() => {
    fetchOnlineCurriculumOptions()
    fetchUsernamePasswordFields()
})

async function fetchOnlineCurriculumOptions() {
    onlineCurriculum.fetching = true

    try {
        const { data } = await axios.get(route('api.configuration.online-curriculum.options'))

        onlineCurriculum.options = data.data
    } catch (e) {
        // @TODO handle error
    } finally {
         onlineCurriculum.fetching = false;
    }
}

function closeOnlineCurriculumOptions() {
    fetchOnlineCurriculumOptions()

    onlineCurriculum.isOpen = false
}

function addOnlineCurriculumOption() {
    onlineCurriculum.options.push({
        label: null,
        name: `online_curriculum_option_${onlineCurriculum.options.length + 1}`,
    })
}

function removeOnlineCurriculumOption() {
    const removed = `online_curriculum_option_${onlineCurriculum.options.length}`

    onlineCurriculum.removed.push(removed)

    onlineCurriculum.options = onlineCurriculum.options.filter(
        (onlineCurriculumOption) => onlineCurriculumOption.name != removed
    )
}

async function saveOnlineCurriculumOptions() {
    const didConfirm = await confirm('Save Online Curriculum Options', {
        text: `Are you sure do you want to save online curriculum options?`
    })

    if (! didConfirm) {
        return
    }

    onlineCurriculum.saving = true;

    Inertia.post(route('api.configuration.online-curriculum.options.save'), {
        options: onlineCurriculum.options,
        removed: onlineCurriculum.removed
    }, {
        onSuccess: () => {
            onlineCurriculum.isOpen = false

            showAlert(`Changes saved!`, {
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false
            })

            fetchOnlineCurriculumOptions()
        },
        onFinish: () => onlineCurriculum.saving = false
    })
}

async function fetchUsernamePasswordFields() {
    usernamePasswordFields.fetching = true

    try {
        const { data } = await axios.get(route('api.configuration.online-curriculum.username-password-fields'))

        usernamePasswordFields.fields = data
    } catch (e) {
        // @TODO handle error
    } finally {
         usernamePasswordFields.fetching = false;
    }
}

function addUsernamePasswordFields() {
    const fieldCount = usernamePasswordFields.fields.length + 1

    const groupName = `username_password_field_${fieldCount}`

    usernamePasswordFields.fields.push({
        fields: [
            {
                title: `Username Field ${fieldCount} Name`,
                name: `username_field_${fieldCount}`,
                label: null,
                type: 'text'
            },
            {
                title: `Password Field ${fieldCount} Name`,
                name: `password_field_${fieldCount}`,
                label: null,
                type: 'text'
            },
            {
                title: `Options Field ${fieldCount} Name`,
                name: `option_field_${fieldCount}`,
                label: 'Assigned to',
                type: 'select'
            },
        ],
        group_name: groupName,
    })
}

function removeUsernamePasswordFields() {
    const removed = `username_password_field_${usernamePasswordFields.fields.length}`

    usernamePasswordFields.removed.push(removed)

    usernamePasswordFields.fields = usernamePasswordFields.fields.filter(
        (usernamePasswordField) => usernamePasswordField.group_name != removed
    )
}

function closeUsernamePasswordFields() {
    usernamePasswordFields.isOpen = false
}

async function saveUsernamePasswordFields() {
    const didConfirm = await confirm('Save Online Curriculum Usernames & Passwords?', {
        text: `Are you sure do you want to save Online Curriculum Usernames & Passwords?`
    })

    if (! didConfirm) {
        return
    }

    usernamePasswordFields.saving = true;

    Inertia.post(route('api.configuration.online-curriculum.username-password-fields.save'), {
        fields: usernamePasswordFields.fields,
        removed: usernamePasswordFields.removed
    }, {
        onSuccess: () => {
            usernamePasswordFields.isOpen = false

            showAlert(`Changes saved!`, {
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false
            })

            fetchUsernamePasswordFields()
        },
        onFinish: () => usernamePasswordFields.saving = false
    })
}
</script>